import { computed, watch } from "vue"

export const CONSENT_COOKIE_NAME = "dapamas_consent"
export const CONSENT_POLICY_VERSION = "1.0.0"

export type ConsentCategory = "necessary" | "functional" | "analytics" | "marketing"
export type ConsentMethod = "accept_all" | "reject_all" | "custom" | "withdrawn"

export interface ConsentChoices {
    necessary: true
    functional: boolean
    analytics: boolean
    marketing: boolean
}

export interface ConsentRecord {
    choices: ConsentChoices
    version: string
    method: ConsentMethod
    timestamp: string
    id: string
}

const DEFAULT_DENIED: ConsentChoices = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
}

function newId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID()
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function buildRecord(choices: ConsentChoices, method: ConsentMethod, prev?: ConsentRecord | null): ConsentRecord {
    return {
        choices,
        version: CONSENT_POLICY_VERSION,
        method,
        timestamp: new Date().toISOString(),
        id: prev?.id ?? newId(),
    }
}

export const useConsent = () => {
    // The cookie is the source of truth for persistence and SSR.
    // `secure` must only be set in production: on localhost (dev) some
    // browsers reject Secure cookies served over http, which silently
    // prevented writes and made the banner appear unresponsive.
    const cookie = useCookie<ConsentRecord | null>(CONSENT_COOKIE_NAME, {
        default: () => null,
        maxAge: 60 * 60 * 24 * 365,
        sameSite: "lax",
        secure: process.env.NODE_ENV === "production",
        path: "/",
    })

    // `useState` gives us a single reactive ref shared across every component
    // that calls useConsent() in the same request. Without this, the banner
    // and the preferences modal each had their own useCookie ref and writes
    // from one were not seen by the other.
    const record = useState<ConsentRecord | null>("consent-record", () => cookie.value)

    // Keep cookie in sync whenever state changes (client only).
    if (import.meta.client) {
        watch(record, (next) => {
            cookie.value = next
        }, { deep: true })
    }

    const hasDecision = computed(() => {
        if (!record.value) return false
        return record.value.version === CONSENT_POLICY_VERSION
    })

    const choices = computed<ConsentChoices>(() => record.value?.choices ?? DEFAULT_DENIED)

    const hasConsent = (category: ConsentCategory): boolean => {
        if (category === "necessary") return true
        if (!record.value) return false
        return record.value.choices[category] === true
    }

    const logProof = async (next: ConsentRecord) => {
        if (!import.meta.client) return
        try {
            await $fetch("/api/consent", {
                method: "POST",
                body: {
                    id: next.id,
                    timestamp: next.timestamp,
                    version: next.version,
                    method: next.method,
                    choices: next.choices,
                },
            })
        } catch {
            // Non-blocking: cookie still holds the user's choice locally.
        }
    }

    const notifyUpdate = (next: ConsentRecord) => {
        if (!import.meta.client) return
        window.dispatchEvent(new CustomEvent("consent:update", { detail: next }))
    }

    const persist = (next: ConsentRecord) => {
        record.value = next
        notifyUpdate(next)
        void logProof(next)
    }

    const acceptAll = () => {
        const next = buildRecord(
            { necessary: true, functional: true, analytics: true, marketing: true },
            "accept_all",
            record.value
        )
        persist(next)
    }

    const rejectAll = () => {
        const next = buildRecord(DEFAULT_DENIED, "reject_all", record.value)
        persist(next)
        clearNonEssentialStorage()
    }

    const save = (partial: Partial<Omit<ConsentChoices, "necessary">>) => {
        const merged: ConsentChoices = {
            necessary: true,
            functional: partial.functional ?? false,
            analytics: partial.analytics ?? false,
            marketing: partial.marketing ?? false,
        }
        const next = buildRecord(merged, "custom", record.value)
        persist(next)
        if (!merged.functional && !merged.analytics && !merged.marketing) {
            clearNonEssentialStorage()
        }
    }

    const withdraw = () => {
        const next = buildRecord(DEFAULT_DENIED, "withdrawn", record.value)
        persist(next)
        clearNonEssentialStorage()
    }

    return {
        record,
        choices,
        hasDecision,
        hasConsent,
        acceptAll,
        rejectAll,
        save,
        withdraw,
    }
}

const ESSENTIAL_COOKIES = new Set<string>([
    CONSENT_COOKIE_NAME,
    "XSRF-TOKEN",
    "dapamas_session",
    "selected-province",
])

function clearNonEssentialStorage() {
    if (!import.meta.client) return
    const host = window.location.hostname
    const parts = host.split(".")
    const domains = new Set<string>([host])
    if (parts.length > 1) domains.add("." + parts.slice(-2).join("."))

    for (const raw of document.cookie.split(";")) {
        const name = raw.split("=")[0]?.trim()
        if (!name || ESSENTIAL_COOKIES.has(name)) continue
        for (const d of domains) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`
        }
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    }
}
